import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { DEVICE_ID } from '@/services/deviceId';
import { isOnline } from '@/services/healthCheck';
import { useAuthStore } from '@/stores/authStore';
import { signItem } from '@/services/offlineSign';
import {
  enqueueItem, getAllQueuedItems, updateQueuedItemStatus, removeQueuedItem,
  saveOfflineWindow, getOfflineWindows, updateOfflineWindow,
} from '@/services/db';

const OFFLINE_START_KEY = 'offlineWindowStart';
const EMAIL_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';

// How long to wait, once online, before auto-syncing — lets a flaky connection settle
// so we don't fire on the first momentary blip. Surfaced as a live countdown.
const SYNC_COOLDOWN_MS = 3 * 60 * 1000;

// The cashier's own JWT — captured at sale time so the queued order can sync under the
// person who made it, with nobody logged in. Cashier `token` first, manager fallback,
// mirroring the online order path.
function activeToken() {
  return localStorage.getItem('token') || localStorage.getItem('managerToken') || '';
}

// Email of the person who rang up the sale, for ERP attribution (passed through unchanged).
function emailFromActiveToken() {
  try {
    const token = activeToken();
    if (!token) return '';
    const [, payload] = token.split('.');
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded[EMAIL_CLAIM] || decoded.email || '';
  } catch {
    return '';
  }
}

export const useOfflineQueueStore = defineStore('offlineQueue', () => {
  const toast = useToast();

  const queue = ref([]);
  const offlineWindows = ref([]);
  const syncModalOpen = ref(false);
  const isSaving = ref(false);
  // Seconds until the next auto-sync attempt; null when no sync is scheduled.
  const syncCountdown = ref(null);

  let _timer = null;
  let _deadline = 0;

  const pendingCount = computed(() =>
    queue.value.filter(e => e.status === 'pending').length);
  const conflictCount = computed(() =>
    queue.value.filter(e => e.status === 'conflict').length);
  const failedCount = computed(() =>
    queue.value.filter(e => e.status === 'failed').length);
  const syncedCount = computed(() =>
    queue.value.filter(e => e.status === 'synced').length);
  const rejectedCount = computed(() =>
    queue.value.filter(e => e.status === 'rejected').length);
  const totalUnsynced = computed(() =>
    queue.value.filter(e => !['synced', 'skipped', 'rejected'].includes(e.status)).length);
  // True whenever the pill should show a sync animation: counting down OR posting.
  const isSyncing = computed(() => isSaving.value || syncCountdown.value != null);

  async function loadQueue() {
    queue.value = await getAllQueuedItems();
  }

  async function loadWindows() {
    offlineWindows.value = await getOfflineWindows();
  }

  async function markOfflineStart() {
    if (localStorage.getItem(OFFLINE_START_KEY)) return;
    const startedAt = new Date().toISOString();
    localStorage.setItem(OFFLINE_START_KEY, startedAt);
    await saveOfflineWindow({
      windowId: crypto.randomUUID(),
      startedAt,
      endedAt: null,
      synced: false,
    });
    await loadWindows();
  }

  async function enqueue({ type, endpoint, method = 'post', body, branchId, submitterEmail }) {
    const localId = crypto.randomUUID();
    const orderNumber = `OL-${localId.slice(0, 8).toUpperCase()}`;

    // Stamp an offline-origin comment carrying the printed order number, so it follows the
    // order into ERP when it syncs. Covers regular and manual orders (server binds Comments
    // case-insensitively, so we normalise any lowercase `comments` into `Comments`).
    let stampedBody = body;
    if (body && typeof body === 'object') {
      const existing = body.Comments || body.comments || [];
      const uid = localStorage.getItem('userId') || '';
      const who = localStorage.getItem('currentUser') || submitterEmail || 'a user';
      // One comment per offline manual-entry item, so the reason + who-added-it reaches ERP.
      const manualComments = (body.ItemList || body.itemList || [])
        .filter((it) => it && it.IsOfflineManualEntry)
        .map((it) => ({
          UserId: uid,
          Text: `Manual item ${it.RetailSku || it.Sku || it.ItemName || ''} added by ${who} because POS was offline`,
        }));
      stampedBody = {
        ...body,
        Comments: [
          ...existing,
          {
            UserId: uid,
            Text: `This order was created offline and was printed with the order number ${orderNumber}`,
          },
          ...manualComments,
        ],
      };
      delete stampedBody.comments;
    }

    const payloadJson = JSON.stringify(stampedBody);
    // Sign the item with this device's HMAC secret (issued at online login) so the server
    // can reject tampered orders. No secret (device not provisioned) → empty hash → server rejects.
    const hash = await signItem(localStorage.getItem('deviceSigningSecret') || '', DEVICE_ID, localId, payloadJson);

    const item = {
      localId,
      orderNumber,                // OL-XXXXXXXX — printed on the receipt + stored server-side
      type,                       // 'order' | 'manual_order'
      endpoint,
      method,
      body: stampedBody,
      payloadJson,
      hash,
      branchId: branchId || localStorage.getItem('branchId') || null,
      submitterEmail: submitterEmail || emailFromActiveToken(),
      authToken: activeToken(),   // the maker's token — used to authorize the eventual sync
      deviceId: DEVICE_ID,
      createdAt: Date.now(),
      createdAtIso: new Date().toISOString(),
      status: 'pending',
    };
    await enqueueItem(item);
    await loadQueue();
    return localId;
  }

  // --- auto-sync scheduling --------------------------------------------------

  function _clearTimer() {
    if (_timer) { clearInterval(_timer); _timer = null; }
    syncCountdown.value = null;
  }

  // Credentials to authorize the SaveQueue batch. The endpoint just needs ONE valid
  // token (attribution is per-item via submitterEmail), so we use the freshest queued
  // item's token, falling back to whoever is logged in now.
  function syncCredentials() {
    // Any unsynced item's stored token can authorize the batch (attribution is per-item),
    // so retry works for failed items even with nobody logged in. Prefer the freshest.
    const unsynced = queue.value.filter(e => !['synced', 'skipped'].includes(e.status));
    const withTok = [...unsynced].reverse().find(e => e.authToken);
    return {
      authToken: withTok?.authToken || activeToken() || '',
      branchId: withTok?.branchId || unsynced[0]?.branchId || localStorage.getItem('branchId') || '',
    };
  }

  // Begin (or leave running) the cooldown countdown. Idempotent — won't restart a
  // countdown that's already ticking or interrupt an in-flight sync.
  function scheduleSync(delayMs = SYNC_COOLDOWN_MS) {
    if (isSaving.value || _timer) return;
    if (pendingCount.value === 0) return;
    _deadline = Date.now() + delayMs;
    syncCountdown.value = Math.ceil(delayMs / 1000);
    _timer = setInterval(() => {
      const remain = Math.max(0, _deadline - Date.now());
      syncCountdown.value = Math.ceil(remain / 1000);
      if (remain <= 0) { _clearTimer(); performSync(); }
    }, 1000);
  }

  function scheduleSyncIfPending() {
    if (pendingCount.value > 0) scheduleSync();
  }

  function cancelSync() {
    _clearTimer();
  }

  // Skip the cooldown and sync immediately (manual "Sync now").
  async function syncNow() {
    _clearTimer();
    await performSync();
  }

  async function performSync() {
    if (isSaving.value) return;
    const { authToken } = syncCredentials();
    if (!authToken) {
      // Nothing we can do without a token (e.g. legacy items captured before per-item
      // tokens, and no one logged in). Stop animating; will retry on next trigger.
      _clearTimer();
      return;
    }
    await saveQueueToServer();
    // If items are still pending the attempt failed — retry after another cooldown.
    if (pendingCount.value > 0) scheduleSync();
  }

  // (Re)sign every pending item with the device's current signing secret. Fixes items
  // queued BEFORE the device was provisioned (empty hash) — or signed with a stale secret
  // — so they aren't rejected on sync. Re-signing the device's own queued orders is
  // legitimate (the signature binds deviceId+localId+payload, all already stored).
  async function resignPending() {
    const secret = localStorage.getItem('deviceSigningSecret');
    if (!secret) return;
    const items = await getAllQueuedItems();
    for (const item of items) {
      if (item.status !== 'pending') continue;
      const payloadJson = item.payloadJson ?? JSON.stringify(item.body);
      const fresh = await signItem(secret, item.deviceId || DEVICE_ID, item.localId, payloadJson);
      if (fresh && fresh !== item.hash) {
        await updateQueuedItemStatus(item.localId, item.status, { hash: fresh });
      }
    }
    await loadQueue();
  }

  // Snapshot every still-pending item to the server. Idempotent server-side.
  async function saveQueueToServer(opts = {}) {
    if (isSaving.value) return null;

    const authStore = useAuthStore();
    // Make sure this device has a signing secret, then (re)sign any pending items with it,
    // so none get rejected for a missing/stale hash.
    await authStore.ensureDeviceRegistered();
    await resignPending();

    const pending = queue.value.filter(e => e.status === 'pending');
    if (pending.length === 0) return null;

    const creds = syncCredentials();
    let authToken = opts.authToken || creds.authToken;
    const branchId = opts.branchId || creds.branchId;
    // Ensure the batch has a valid token: after a long offline window the maker's 24h
    // JWT may have lapsed — refresh it via a stored refresh token so the queue still
    // drains with nobody interactively logged in (FIX-19).
    authToken = await authStore.ensureValidSyncToken(authToken);
    if (!authToken || !branchId) return null;

    isSaving.value = true;
    const startedAt = localStorage.getItem(OFFLINE_START_KEY)
      || new Date(Date.now() - 60_000).toISOString();
    const endedAt = new Date().toISOString();

    try {
      const payload = {
        deviceId: DEVICE_ID,
        branchId,
        offlineStartedAtUtc: startedAt,
        offlineEndedAtUtc: endedAt,
        items: pending.map(item => ({
          localId: item.localId,
          offlineOrderNumber: item.orderNumber || null,
          type: item.type,
          endpoint: item.endpoint,
          method: item.method,
          payloadJson: item.payloadJson ?? JSON.stringify(item.body),
          hash: item.hash || '',
          submitterEmail: item.submitterEmail || '',
          createdAtUtc: item.createdAtIso || new Date(item.createdAt).toISOString(),
        })),
      };

      const res = await axios.post('/api/OfflineSync/SaveQueue', payload, {
        headers: { Authorization: `Bearer ${authToken}`, 'store-id': branchId },
      });

      const offlineWindowId = res.data?.offlineWindowId ?? null;

      for (const item of pending) {
        await updateQueuedItemStatus(item.localId, 'processing', { offlineWindowId });
      }
      await loadQueue();

      localStorage.removeItem(OFFLINE_START_KEY);
      const localWindow = offlineWindows.value.find(w => !w.synced);
      if (localWindow) {
        await updateOfflineWindow(localWindow.windowId, { endedAt, synced: true, serverWindowId: offlineWindowId });
        await loadWindows();
      }

      toast.success(`Syncing ${pending.length} offline transaction(s)…`);
      // Backstop SignalR: poll status until these resolve, so the UI never sticks.
      startReconcileLoop();
      return offlineWindowId;
    } catch (err) {
      toast.error('Could not sync offline queue — will retry shortly.');
      return null;
    } finally {
      isSaving.value = false;
    }
  }

  // --- reconciliation backstop -----------------------------------------------
  // SignalR is the fast path, but events can be missed (the order can finish before
  // our socket joins the branch group, or after a reconnect). Poll the authoritative
  // server status for any non-terminal item so the UI never gets stuck on "processing".
  let _reconcileTimer = null;

  function stopReconcileLoop() {
    if (_reconcileTimer) { clearInterval(_reconcileTimer); _reconcileTimer = null; }
  }

  async function reconcile() {
    const ids = queue.value
      .filter(e => !['synced', 'skipped'].includes(e.status))
      .map(e => e.localId);
    if (ids.length === 0) return;
    const { authToken, branchId } = syncCredentials();
    if (!authToken || !branchId) return;
    try {
      const res = await axios.post('/api/OfflineSync/Status',
        { deviceId: DEVICE_ID, localIds: ids },
        { headers: { Authorization: `Bearer ${authToken}`, 'store-id': branchId } });
      for (const s of res.data || []) {
        await applyServerStatus({
          localId: s.localId, status: s.status,
          serverInvoiceId: s.serverInvoiceId, detail: s.conflictReason,
        });
      }
    } catch { /* transient — next tick retries */ }
  }

  // Polls until nothing is left processing (or a ~3min safety cap). Idempotent.
  function startReconcileLoop() {
    if (_reconcileTimer) return;
    let ticks = 0;
    const run = async () => {
      ticks++;
      if (isOnline.value) await reconcile();
      if (ticks >= 45 || !queue.value.some(e => e.status === 'processing')) stopReconcileLoop();
    };
    run();
    _reconcileTimer = setInterval(run, 4000);
  }

  // Applied from the SignalR offline_sync_status handler in App.vue.
  async function applyServerStatus({ localId, status, serverInvoiceId, detail }) {
    const item = queue.value.find(e => e.localId === localId);
    if (!item) return;
    await updateQueuedItemStatus(localId, status, {
      ...(serverInvoiceId ? { serverInvoiceId } : {}),
      ...(detail ? { conflictReason: detail } : {}),
    });
    await loadQueue();

    if (status === 'synced' && serverInvoiceId) {
      updateReceiptWithServerInvoiceId(localId, serverInvoiceId);
    }
  }

  function updateReceiptWithServerInvoiceId(localId, serverInvoiceId) {
    try {
      const receipts = JSON.parse(localStorage.getItem('savedReceipts') || '[]');
      const idx = receipts.findIndex(r => r.offlineLocalId === localId);
      if (idx !== -1) {
        receipts[idx].invoiceId = serverInvoiceId;
        receipts[idx].syncedAt = Date.now();
        localStorage.setItem('savedReceipts', JSON.stringify(receipts));
      }
    } catch { /* best effort */ }
  }

  async function resolveConflict({ offlineWindowId, localId, resolution, matchedSku }) {
    try {
      const { authToken, branchId } = syncCredentials();
      await axios.post('/api/OfflineSync/ResolveConflict',
        { offlineWindowId, localId, resolution, matchedSku },
        { headers: { Authorization: `Bearer ${authToken}`, 'store-id': branchId } });
      const status = resolution === 'skip' ? 'skipped' : 'processing';
      await updateQueuedItemStatus(localId, status);
      await loadQueue();
    } catch (err) {
      toast.error('Could not submit conflict resolution.');
    }
  }

  // Re-attempt items that failed server-side (e.g. after a fix). Resets them to pending
  // on the server and re-enqueues; statuses then flow back over SignalR.
  async function retryFailed() {
    if (failedCount.value === 0) return;
    const { authToken, branchId } = syncCredentials();
    if (!authToken || !branchId) {
      toast.error('No saved credentials to retry — log in once to retry these.');
      return;
    }
    isSaving.value = true;
    try {
      await axios.post('/api/OfflineSync/RetryFailed',
        { deviceId: DEVICE_ID },
        { headers: { Authorization: `Bearer ${authToken}`, 'store-id': branchId } });
      // Reflect the reset locally; SignalR will move them to processing→synced.
      for (const item of queue.value.filter(e => e.status === 'failed')) {
        await updateQueuedItemStatus(item.localId, 'processing');
      }
      await loadQueue();
      toast.success('Retrying failed transaction(s)…');
    } catch (err) {
      toast.error('Could not retry — will stay queued.');
    } finally {
      isSaving.value = false;
    }
  }

  async function skipConflict(localId) {
    await updateQueuedItemStatus(localId, 'skipped');
    await loadQueue();
  }

  async function removeItem(localId) {
    await removeQueuedItem(localId);
    await loadQueue();
  }

  // Clear all synced confirmations at once (receipts remain in saved-receipts).
  async function dismissSynced() {
    const synced = queue.value.filter(e => e.status === 'synced');
    for (const item of synced) await removeQueuedItem(item.localId);
    await loadQueue();
  }

  // Clear rejected items (signature/tamper-rejected — terminal, they will never sync).
  // The printed receipt stays in saved-receipts.
  async function dismissRejected() {
    const rejected = queue.value.filter(e => e.status === 'rejected');
    for (const item of rejected) await removeQueuedItem(item.localId);
    await loadQueue();
  }

  async function markStatus(localId, status, extra = {}) {
    await updateQueuedItemStatus(localId, status, extra);
    await loadQueue();
  }

  return {
    queue, offlineWindows, syncModalOpen, isSaving, syncCountdown,
    pendingCount, conflictCount, failedCount, syncedCount, rejectedCount, totalUnsynced, isSyncing,
    loadQueue, loadWindows, markOfflineStart, enqueue,
    scheduleSync, scheduleSyncIfPending, cancelSync, syncNow,
    saveQueueToServer, applyServerStatus, resolveConflict, skipConflict,
    retryFailed, reconcile, startReconcileLoop, removeItem, dismissSynced, dismissRejected, markStatus,
  };
});
